import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as EditIcon } from "../../../../../../assets/edit-pencil.svg";

import NorthIcon from "@mui/icons-material/North";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLanguages } from "../../../../../../context/LanguagesContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { useAuth } from "../../../../../../context/AuthContext";
import { useToast } from "../../../../../../context/ToastContext";
import { useTranslation } from "../../../../../../context/TranslationContext";
import { ConfirmModal } from "../../../../../../components/ConfirmModal/ConfirmModal";
import {
  deleteTranslationOverride,
  updateAttributeTranslation,
} from "../../../../../../services/api/apiClient";
import {
  AttributeValues,
  TabId,
  TabSectionKey,
  TranslationManageAttributeResult,
  UpdateTranslation,
} from "../../../../../../services/api/types/portalConfiguration/translations";
import { brand, gray } from "../../../../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../../../../utils/errorUtils";
import { fromCamelCase } from "../../../../../../utils/helpers";
import { MAX_LENGTH_DESCRIPTION } from "../../../../../../utils/inputLimits";

interface EditModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  tabId: TabId;
  tabSectionKey: TabSectionKey;
  attribute: TranslationManageAttributeResult;
  isMain: boolean;
  onSaved: () => void;
}

const orderLanguages = (codes: string[]) =>
  [...codes].sort((a, b) =>
    a === "en" ? -1 : b === "en" ? 1 : a.localeCompare(b),
  );

export const EditModal: React.FC<EditModalProps> = ({
  openModal,
  setOpenModal,
  tabId,
  tabSectionKey,
  attribute,
  isMain,
  onSaved,
}) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [resetLangPending, setResetLangPending] = useState<string>();
  const [resetLoading, setResetLoading] = useState(false);
  const { languages, getLanguageDetails } = useLanguages();
  const { portals } = usePortal();
  const { auth } = useAuth();
  const { showToast } = useToast();
  const { updateTranslationSection } = useTranslation();

  const languageCodes = orderLanguages(Object.keys(attribute.values));
  const attributeLabel =
    attribute.values.en?.effectiveValue ||
    fromCamelCase(attribute.attributeKey);
  const isMobileOnly = attribute.attributeKey.startsWith("mobile");

  useEffect(() => {
    const initialValues: Record<string, string> = {};
    languageCodes.forEach((code) => {
      initialValues[code] = attribute.values[code]?.effectiveValue ?? "";
    });
    setEditedValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute, openModal]);

  const handleInputChange = (langCode: string, newValue: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [langCode]: newValue,
    }));
  };

  const updateTranslation = async () => {
    const attributeValues: AttributeValues[] = Object.entries(editedValues)
      .filter(
        ([langCode, value]) =>
          value !== (attribute.values[langCode]?.effectiveValue ?? ""),
      )
      .map(([langCode, value]) => ({
        language: langCode,
        attributeValue: value,
      }));

    if (attributeValues.length === 0) {
      setOpenModal(false);
      return;
    }

    try {
      setButtonLoading(true);
      if (auth.user && portals.selectedPortal) {
        const body: UpdateTranslation = {
          attributeKey: attribute.attributeKey,
          adminUser: auth.user.UserPortalUid,
          attributeValues,
        };
        await updateAttributeTranslation(
          portals.selectedPortal.portalUid,
          tabId,
          tabSectionKey,
          body,
        );

        const selectedLangValue = body.attributeValues.find(
          (v) => v.language === languages.selectedLanguage,
        )?.attributeValue;
        if (selectedLangValue !== undefined) {
          updateTranslationSection(tabId, tabSectionKey, {
            [attribute.attributeKey]: selectedLangValue,
          });
        }

        onSaved();
      }
    } catch (error: any) {
      showToast(
        extractErrorMessage(error, "Failed to update translation"),
        "error",
      );
    } finally {
      setButtonLoading(false);
      setOpenModal(false);
    }
  };

  const handleResetConfirm = async () => {
    if (!portals.selectedPortal || !resetLangPending) return;
    try {
      setResetLoading(true);
      await deleteTranslationOverride(
        portals.selectedPortal.portalUid,
        tabId,
        tabSectionKey,
        attribute.attributeKey,
        resetLangPending,
      );
      onSaved();
    } catch (error: any) {
      showToast(
        extractErrorMessage(error, "Failed to reset translation"),
        "error",
      );
    } finally {
      setResetLoading(false);
      setResetLangPending(undefined);
      setOpenModal(false);
    }
  };

  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
            padding: "16px",
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5">{attributeLabel}</Typography>
              {isMobileOnly && (
                <Typography variant="caption" color="text.secondary">
                  (Only on mobile device)
                </Typography>
              )}
            </Box>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{
              paddingBottom: "1rem",
              borderBottom: `1px solid ${gray[300]}`,
            }}
            pb={"2rem"}
          >
            Translate text to selected languages
          </Typography>
          <Stack spacing={2} sx={{ marginTop: "1rem" }}>
            {languageCodes.map((langCode) => {
              const detail = attribute.values[langCode];
              const isEdited = !isMain && !!detail?.isOverridden;
              return (
                <Stack spacing={0.5} key={langCode}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2">
                        {langCode === "en"
                          ? "English (Base)"
                          : (getLanguageDetails(langCode)?.name ?? langCode)}
                      </Typography>
                      {!isMain && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={
                            isEdited ? (
                              <EditIcon
                                style={{
                                  color: brand[600],
                                  width: 14,
                                  height: 14,
                                }}
                              />
                            ) : (
                              <NorthIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label={isEdited ? "Edited" : "Inherited"}
                          sx={
                            isEdited
                              ? {
                                  borderColor: brand[200],
                                  bgcolor: brand[50],
                                  "& .MuiChip-label": {
                                    color: brand[700],
                                  },
                                }
                              : {
                                  borderColor: gray[300],
                                  "& .MuiChip-label": {
                                    color: gray[600],
                                  },
                                }
                          }
                        />
                      )}
                    </Box>
                    {isEdited && (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setResetLangPending(langCode)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: gray[700],
                          fontSize: 14,
                          fontWeight: 600,
                          p: 0,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <RestartAltIcon fontSize="small" />
                        Reset to inherited
                      </Box>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    type="text"
                    value={editedValues[langCode] || ""}
                    onChange={(e) =>
                      handleInputChange(langCode, e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    slotProps={{
                      htmlInput: { maxLength: MAX_LENGTH_DESCRIPTION },
                    }}
                  />
                </Stack>
              );
            })}
            <Divider />
            {buttonLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Button
                onClick={updateTranslation}
                fullWidth
                sx={{
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  "&:hover": {
                    backgroundColor: `${brand[700]} !important`,
                  },
                }}
              >
                <Typography>Update</Typography>
              </Button>
            )}
          </Stack>
        </Box>
      </Modal>
      {resetLangPending && (
        <ConfirmModal
          openConfirmModal={!!resetLangPending}
          setOpenConfirmModal={(open) => {
            if (!open) setResetLangPending(undefined);
          }}
          modalTitle="Reset to inherited value?"
          modalSubtitle={`This will overwrite the portal's translation for "${attributeLabel}" in ${resetLangPending === "en" ? "English" : (getLanguageDetails(resetLangPending)?.name ?? resetLangPending)} with the main portal's current default text. Are you sure you want to reset this value?`}
          confirmButtonClick={handleResetConfirm}
          confirmButtonText="Yes, I'm sure"
          cancelButtonText="Cancel"
          modalLoading={resetLoading}
        />
      )}
    </>
  );
};
